import { getTable } from '../../requests/documentDatas';
import { tableFetchKey } from './webTableRefresh';

/**
 * Cache des réponses brutes de l'API, par **table et axes donnée**.
 *
 * C'est ce qui distingue les deux natures de réglage d'un tableau
 * paramétrable, sans avoir à les comparer : les axes donnée entrent dans la
 * clé (ils changent l'URL), les overrides de présentation n'y entrent pas.
 * Masquer, renommer, réordonner ou redimensionner une colonne retombe donc sur
 * la même clé et se rejoue sur la réponse déjà lue — **sans requête**. Changer
 * de mode ou de variante produit une clé neuve et déclenche la lecture.
 *
 * Registre module-level, hors du store Puck, comme les autres canaux du
 * webviewer.
 */

const cache = new Map();
const inFlight = new Map();

/**
 * @param {{ id: string|number, type: string }} source - document porteur
 * @param {string} tableName
 * @param {object|null} tableConfig
 * @returns {Promise<object|null>} réponse brute, ou null si la source manque
 */
export function readTable(
  source, tableName, tableConfig, { split = false, fetchTable = getTable } = {},
) {
  if (!source?.id || !source?.type || !tableName) return Promise.resolve(null);

  // La source entre dans la clé : deux blocs peuvent viser la même table sur
  // deux opportunités différentes.
  const key = tableFetchKey(tableName, tableConfig, source, split);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inFlight.has(key)) return inFlight.get(key);

  const promise = fetchTable(source, tableName, { split, tableConfig })
    .then((table) => {
      if (table) cache.set(key, table);
      return table;
    })
    .finally(() => inFlight.delete(key));

  inFlight.set(key, promise);
  return promise;
}

/**
 * Oublie ce qui a été lu — après un « Rafraîchir les variables », sans quoi
 * l'écran de configuration continuerait de rejouer une réponse périmée.
 */
export function clearTableRawCache() {
  cache.clear();
  inFlight.clear();
}
